import { Injectable } from '@angular/core';
import { IExchangeModel } from 'src/app/logic/exchanges/model/exchange.model';

type QuoteType = 'from' | 'to';

export const MIN_RESERVATION_USD = 1;
export const PHONE_VERIFICATION_REQUIRED_USD = 1000;

@Injectable({
  providedIn: 'root',
})
export class ExchangeRateCalculatorService {
  private readonly localCurrencyCode = 'MXN';

  convertFromAmount(
    fromExchange: IExchangeModel,
    toExchange: IExchangeModel,
    fromAmount: number,
  ): number {
    const amount = Number(fromAmount) || 0;
    if (!fromExchange?.currency?.code || !toExchange?.currency?.code) {
      return 0;
    }

    const fromCode = fromExchange.currency.code.toUpperCase();
    const toCode = toExchange.currency.code.toUpperCase();

    if (fromCode === this.localCurrencyCode && toCode !== this.localCurrencyCode) {
      const foreignSell = Number(toExchange.sell) || 0;
      return foreignSell > 0 ? amount / foreignSell : 0;
    }

    if (toCode === this.localCurrencyCode && fromCode !== this.localCurrencyCode) {
      const foreignBuy = Number(fromExchange.buy) || 0;
      return foreignBuy > 0 ? amount * foreignBuy : 0;
    }

    // Foreign cross: client hands over `from` (we buy at from.buy) and receives `to` (we sell at to.sell).
    const fromBuy = Number(fromExchange.buy) || 0;
    const toSell = Number(toExchange.sell) || 0;
    return fromBuy > 0 && toSell > 0 ? (amount * fromBuy) / toSell : 0;
  }

  convertToAmount(
    fromExchange: IExchangeModel,
    toExchange: IExchangeModel,
    toAmount: number,
  ): number {
    const amount = Number(toAmount) || 0;
    if (!fromExchange?.currency?.code || !toExchange?.currency?.code) {
      return 0;
    }

    const fromCode = fromExchange.currency.code.toUpperCase();
    const toCode = toExchange.currency.code.toUpperCase();

    if (fromCode === this.localCurrencyCode && toCode !== this.localCurrencyCode) {
      const foreignSell = Number(toExchange.sell) || 0;
      return foreignSell > 0 ? amount * foreignSell : 0;
    }

    if (toCode === this.localCurrencyCode && fromCode !== this.localCurrencyCode) {
      const foreignBuy = Number(fromExchange.buy) || 0;
      return foreignBuy > 0 ? amount / foreignBuy : 0;
    }

    const fromBuy = Number(fromExchange.buy) || 0;
    const toSell = Number(toExchange.sell) || 0;
    return fromBuy > 0 && toSell > 0 ? (amount * toSell) / fromBuy : 0;
  }

  getFromAmountForQuote(quote: {
    from: IExchangeModel;
    to: IExchangeModel;
    amount: number;
    type: QuoteType;
  }): number {
    if (!quote) {
      return 0;
    }

    return quote.type === 'from'
      ? Number(quote.amount) || 0
      : this.convertToAmount(quote.from, quote.to, quote.amount);
  }

  getToAmountForQuote(quote: {
    from: IExchangeModel;
    to: IExchangeModel;
    amount: number;
    type: QuoteType;
  }): number {
    if (!quote) {
      return 0;
    }

    return quote.type === 'to'
      ? Number(quote.amount) || 0
      : this.convertFromAmount(quote.from, quote.to, quote.amount);
  }

  /**
   * USD notional for reservation minimums and document thresholds.
   */
  getUsdNotionalForQuote(
    quote: {
      from: IExchangeModel;
      to: IExchangeModel;
      amount: number;
      type: QuoteType;
    },
    exchanges?: IExchangeModel[],
  ): number {
    if (!quote?.from?.currency?.code || !quote?.to?.currency?.code) {
      return 0;
    }

    const fromCode = quote.from.currency.code.toUpperCase();
    const toCode = quote.to.currency.code.toUpperCase();
    const fromAmt = this.getFromAmountForQuote(quote);
    const toAmt = this.getToAmountForQuote(quote);
    const usdSell = this.getUsdSellRate(exchanges, quote);

    if (fromCode === 'USD') {
      return fromAmt;
    }

    if (toCode === 'USD') {
      return toAmt;
    }

    if (usdSell <= 0) {
      return 0;
    }

    if (fromCode !== this.localCurrencyCode && fromAmt > 0) {
      const buyRate = Number(quote.from.buy) || 0;
      return buyRate > 0 ? (fromAmt * buyRate) / usdSell : 0;
    }

    if (toCode !== this.localCurrencyCode && toAmt > 0) {
      const sellRate = Number(quote.to.sell) || 0;
      return sellRate > 0 ? (toAmt * sellRate) / usdSell : 0;
    }

    if (
      fromCode !== this.localCurrencyCode &&
      toCode !== this.localCurrencyCode &&
      fromAmt > 0
    ) {
      const fromSell = Number(quote.from.sell) || 0;
      return fromSell > 0 ? (fromAmt * fromSell) / usdSell : 0;
    }

    return 0;
  }

  meetsMinimumReservationUsd(
    quote: {
      from: IExchangeModel;
      to: IExchangeModel;
      amount: number;
      type: QuoteType;
    },
    exchanges?: IExchangeModel[],
  ): boolean {
    return (
      this.getUsdNotionalForQuote(quote, exchanges) >= MIN_RESERVATION_USD
    );
  }

  requiresVerifiedPhoneForQuote(
    quote: {
      from: IExchangeModel;
      to: IExchangeModel;
      amount: number;
      type: QuoteType;
    },
    exchanges: IExchangeModel[] | undefined,
    isPhoneVerified: boolean,
  ): boolean {
    if (isPhoneVerified) {
      return false;
    }
    return (
      this.getUsdNotionalForQuote(quote, exchanges) >=
      PHONE_VERIFICATION_REQUIRED_USD
    );
  }

  private getUsdSellRate(
    exchanges: IExchangeModel[] | undefined,
    quote: {
      from: IExchangeModel;
      to: IExchangeModel;
    },
  ): number {
    const fromUsd =
      quote.from.currency?.code?.toUpperCase() === 'USD'
        ? Number(quote.from.sell) || 0
        : 0;
    const toUsd =
      quote.to.currency?.code?.toUpperCase() === 'USD'
        ? Number(quote.to.sell) || 0
        : 0;
    if (fromUsd > 0) {
      return fromUsd;
    }
    if (toUsd > 0) {
      return toUsd;
    }

    const usdExchange = exchanges?.find(
      (exchange) => exchange.currency?.code?.toUpperCase() === 'USD',
    );
    return Number(usdExchange?.sell) || 0;
  }
}
